import React, { useEffect, useState } from "react";

import {
  useFetchBiBreakdownsQuery,
  useFetchBiInsightsQuery,
  useFetchBiInventoryQuery,
  useFetchBiMonthlyQuery,
  useFetchBiOverviewQuery,
  useFetchBiProductsQuery,
  useFetchBiProfitabilityQuery,
} from "../../../api/analyticsApi";
import { Btn, Icon, TextInput } from "../../catalogProductStudio/edit/StudioUi";
import { BarList, DataNotes, DataState, DataTable, ExportButton, Panel } from "../AnalyticsUi";
import { addDays, fill, formatDate, formatMoney, formatNumber, formatPercent, monthRange, todayKey } from "../analyticsModel";
import { InsightCard } from "./InsightsSection";
import { OverviewKpis } from "./OverviewSection";
import { productColumns } from "./ProductsSection";

const EXPORTS = ["transactions", "products", "variants", "categories", "monthly", "inventory"];

/** Toggles a print-only mode so only the open report reaches paper. */
const printReport = () => {
  document.body.classList.add("an-print-mode");
  const done = () => {
    document.body.classList.remove("an-print-mode");
    window.removeEventListener("afterprint", done);
  };
  window.addEventListener("afterprint", done);
  window.print();
  setTimeout(done, 1000);
};

const ReportFrame = ({ title, subtitle, onClose, tt, lang, children }) => (
  <div className="an-report" role="region" aria-label={title}>
    <div className="an-report__toolbar an-noprint">
      <Btn variant="primary" icon="printer" onClick={printReport}>
        {tt("print")}
      </Btn>
      <Btn icon="x" onClick={onClose}>
        {tt("closeReport")}
      </Btn>
    </div>
    <header className="an-report__head">
      <div>
        <p className="an-report__brand">Black &amp; White</p>
        <h2 className="an-report__title">{title}</h2>
        <p className="an-report__subtitle">{subtitle}</p>
      </div>
      <p className="an-muted an-small">{fill(tt("generatedAt"), { date: formatDate(new Date().toISOString(), lang, { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) })}</p>
    </header>
    {children}
  </div>
);

const MonthlyReport = ({ month, tt, lang, costVisible, onClose }) => {
  const range = monthRange(month);
  const overview = useFetchBiOverviewQuery({ ...range, granularity: "week" });
  const products = useFetchBiProductsQuery({ ...range, sort: "netRevenue", direction: "desc", page: 1, pageSize: 10, performance: "selling" });
  const insights = useFetchBiInsightsQuery(range);
  const profitability = useFetchBiProfitabilityQuery({ ...range, granularity: "week" }, { skip: !costVisible });
  const subtitle = formatDate(range.dateFrom, lang, { month: "long", year: "numeric" });

  return (
    <ReportFrame title={tt("report_monthly")} subtitle={subtitle} onClose={onClose} tt={tt} lang={lang}>
      <DataState query={overview} tt={tt}>
        {overview.data ? (
          <>
            <p className="an-muted an-small">
              {fill(tt("comparedTo"), { from: formatDate(overview.data.previousRange.dateFrom, lang), to: formatDate(overview.data.previousRange.dateTo, lang) })}
            </p>
            <OverviewKpis data={overview.data} tt={tt} lang={lang} />
            <DataNotes notes={overview.data.notes} tt={tt} lang={lang} />
            <div className="an-grid an-grid--2">
              <Panel title={tt("byChannel")}>
                <BarList
                  rows={overview.data.channels.map((row) => ({ ...row, key: row.channel }))}
                  valueKey="revenue"
                  labelOf={(row) => tt(`channel_${row.channel}`)}
                  format={(value, row) => `${formatMoney(value, lang)} · ${formatPercent(row.share, lang)}`}
                />
              </Panel>
              <Panel title={tt("byCategory")}>
                <BarList
                  rows={overview.data.categories}
                  valueKey="netRevenue"
                  labelOf={(row) => row.label || tt("uncategorised")}
                  format={(value, row) => `${formatMoney(value, lang)} · ${formatPercent(row.share, lang)}`}
                  emptyText={tt("emptyTitle")}
                />
              </Panel>
            </div>
          </>
        ) : null}
      </DataState>
      {costVisible && profitability.data ? (
        <Panel title={tt("statementTitle")} hint={tt("statementHint")}>
          <dl className="an-facts">
            {[
              ["line_netRevenue", formatMoney(profitability.data.statement.current.netRevenue, lang)],
              ["line_cogsSold", formatMoney(profitability.data.statement.current.cost?.netCogs, lang)],
              ["line_grossProfit", formatMoney(profitability.data.statement.current.cost?.grossProfit, lang)],
              ["line_grossMargin", formatPercent(profitability.data.statement.current.cost?.grossMargin, lang)],
            ].map(([key, value]) => (
              <div key={key}>
                <dt>{tt(key)}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      ) : null}
      <Panel title={tt("topProducts")}>
        <DataState query={products} tt={tt}>
          {products.data ? (
            <DataTable rows={products.data.items} rowKey={(row) => row.productId} emptyText={tt("emptyTitle")} columns={productColumns({ tt, lang, costVisible, withLastSold: false })} />
          ) : null}
        </DataState>
      </Panel>
      <Panel title={tt("insightsTitle")}>
        <DataState query={insights} tt={tt} isEmpty={insights.data && !insights.data.insights.length} emptyTitle={tt("insightsEmpty")}>
          {insights.data ? (
            <div className="an-insights">
              {insights.data.insights.map((insight) => (
                <InsightCard key={insight.id} insight={insight} tt={tt} lang={lang} defaultOpen />
              ))}
            </div>
          ) : null}
        </DataState>
      </Panel>
    </ReportFrame>
  );
};

const ProductReport = ({ range, tt, lang, costVisible, onClose }) => {
  const products = useFetchBiProductsQuery({ dateFrom: range.dateFrom, dateTo: range.dateTo, sort: "netRevenue", direction: "desc", page: 1, pageSize: 50 });
  const breakdowns = useFetchBiBreakdownsQuery({ dateFrom: range.dateFrom, dateTo: range.dateTo });
  const subtitle = `${formatDate(range.dateFrom, lang)} – ${formatDate(range.dateTo, lang)}`;
  return (
    <ReportFrame title={tt("report_products")} subtitle={subtitle} onClose={onClose} tt={tt} lang={lang}>
      <Panel title={tt("categories")}>
        <DataState query={breakdowns} tt={tt}>
          {breakdowns.data ? (
            <DataTable
              rows={breakdowns.data.categories}
              rowKey={(row) => row.key}
              emptyText={tt("emptyTitle")}
              columns={[
                { key: "label", label: tt("category"), render: (row) => row.label || tt("uncategorised") },
                { key: "units", label: tt("col_netUnits"), align: "end", render: (row) => formatNumber(row.netUnits, lang) },
                { key: "net", label: tt("col_netRevenue"), align: "end", render: (row) => formatMoney(row.netRevenue, lang) },
                { key: "share", label: tt("share"), align: "end", render: (row) => formatPercent(row.share, lang) },
                { key: "returns", label: tt("col_returnRate"), align: "end", render: (row) => formatPercent(row.returnRate, lang) },
              ]}
            />
          ) : null}
        </DataState>
      </Panel>
      <Panel title={tt("productsTitle")}>
        <DataState query={products} tt={tt}>
          {products.data ? (
            <DataTable rows={products.data.items} rowKey={(row) => row.productId} emptyText={tt("emptyTitle")} columns={productColumns({ tt, lang, costVisible, withLastSold: false })} />
          ) : null}
        </DataState>
      </Panel>
    </ReportFrame>
  );
};

const InventoryReport = ({ tt, lang, costVisible, onClose }) => {
  const inventory = useFetchBiInventoryQuery({ lowStockThreshold: 2, deadStockDays: 90 });
  const data = inventory.data;
  const listColumns = [
    { key: "variant", label: tt("col_variant"), render: (row) => `${row.productTitle} · ${[row.color, row.size].filter(Boolean).join(" / ")}` },
    { key: "available", label: tt("col_available"), align: "end", render: (row) => formatNumber(row.available, lang) },
    { key: "units30", label: tt("col_units30"), align: "end", render: (row) => formatNumber(row.units30, lang) },
    { key: "value", label: tt("retailValue"), align: "end", render: (row) => formatMoney(row.retailValue, lang) },
  ];
  return (
    <ReportFrame title={tt("report_inventory")} subtitle={tt("inventorySnapshot")} onClose={onClose} tt={tt} lang={lang}>
      <DataState query={inventory} tt={tt}>
        {data ? (
          <>
            <dl className="an-facts">
              {[
                ["unitsOnHand", formatNumber(data.summary.unitsOnHand, lang)],
                ["retailValue", formatMoney(data.summary.retailValue, lang)],
                ...(costVisible ? [["costValue", formatMoney(data.summary.costValue, lang)]] : []),
                ["turnover", `${formatNumber(data.summary.unitTurnover, lang, 2)}×`],
                ["flag_low", formatNumber(data.summary.lowStock, lang)],
                ["flag_out", formatNumber(data.summary.outOfStock, lang)],
                ["flag_dead", `${formatNumber(data.summary.deadStock, lang)} · ${formatMoney(data.summary.deadStockRetailValue, lang)}`],
                ["flag_high_demand", formatNumber(data.summary.highDemandLowStock, lang)],
              ].map(([key, value]) => (
                <div key={key}>
                  <dt>{tt(key)}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <DataNotes notes={data.notes} tt={tt} lang={lang} />
            <Panel title={tt("byCategory")}>
              <BarList
                rows={data.distribution.categories}
                valueKey="units"
                labelOf={(row) => row.label || tt("uncategorised")}
                format={(value, row) => `${formatNumber(value, lang)} · ${formatMoney(row.retailValue, lang)}`}
                emptyText={tt("emptyTitle")}
              />
            </Panel>
            <Panel title={tt("highDemandList")}>
              <DataTable dense rows={data.lists.highDemand} rowKey={(row) => row.variantId} emptyText={tt("insightsEmpty")} columns={listColumns} />
            </Panel>
            <Panel title={tt("lowStockList")}>
              <DataTable dense rows={data.lists.lowStock} rowKey={(row) => row.variantId} emptyText={tt("insightsEmpty")} columns={listColumns} />
            </Panel>
            <Panel title={tt("deadStockList")}>
              <DataTable dense rows={data.lists.deadStock} rowKey={(row) => row.variantId} emptyText={tt("insightsEmpty")} columns={listColumns} />
            </Panel>
          </>
        ) : null}
      </DataState>
    </ReportFrame>
  );
};

const MonthlyTable = ({ range, tt, lang, costVisible }) => {
  const query = useFetchBiMonthlyQuery({ dateFrom: range.dateFrom, dateTo: range.dateTo });
  return (
    <Panel title={tt("monthlyTable")} actions={<ExportButton dataset="monthly" params={{ dateFrom: range.dateFrom, dateTo: range.dateTo }} tt={tt} />}>
      <DataState query={query} tt={tt}>
        {query.data ? (
          <DataTable
            rows={query.data.months}
            rowKey={(row) => row.month}
            emptyText={tt("emptyTitle")}
            columns={[
              { key: "month", label: tt("month"), render: (row) => formatDate(`${row.month}-01`, lang, { month: "long", year: "numeric" }) },
              { key: "sales", label: tt("kpi_salesCount"), align: "end", render: (row) => formatNumber(row.salesCount, lang) },
              { key: "revenue", label: tt("revenue"), align: "end", render: (row) => formatMoney(row.revenue, lang) },
              { key: "refunds", label: tt("refunds"), align: "end", render: (row) => formatMoney(row.refunds, lang) },
              { key: "net", label: tt("col_netRevenue"), align: "end", render: (row) => <strong>{formatMoney(row.netRevenue, lang)}</strong> },
              { key: "aov", label: tt("kpi_averageOrderValue"), align: "end", render: (row) => formatMoney(row.averageOrderValue, lang) },
              ...(costVisible
                ? [
                    { key: "profit", label: tt("col_profit"), align: "end", render: (row) => formatMoney(row.cost?.grossProfit, lang) },
                    { key: "margin", label: tt("col_margin"), align: "end", render: (row) => formatPercent(row.cost?.grossMargin, lang) },
                  ]
                : []),
            ]}
          />
        ) : null}
      </DataState>
    </Panel>
  );
};

const ReportsSection = ({ range, tt, lang, costVisible }) => {
  const lastMonth = addDays(`${todayKey().slice(0, 7)}-01`, -1).slice(0, 7);
  const [month, setMonth] = useState(lastMonth);
  const [open, setOpen] = useState(null);
  useEffect(() => () => document.body.classList.remove("an-print-mode"), []);

  if (open === "monthly") return <MonthlyReport month={month} tt={tt} lang={lang} costVisible={costVisible} onClose={() => setOpen(null)} />;
  if (open === "products") return <ProductReport range={range} tt={tt} lang={lang} costVisible={costVisible} onClose={() => setOpen(null)} />;
  if (open === "inventory") return <InventoryReport tt={tt} lang={lang} costVisible={costVisible} onClose={() => setOpen(null)} />;

  return (
    <>
      <div className="an-grid an-grid--3">
        <Panel title={tt("report_monthly")} hint={tt("report_monthly_hint")}>
          <label className="an-inline-field">
            <span>{tt("month")}</span>
            <TextInput type="month" value={month} max={todayKey().slice(0, 7)} onChange={(value) => value && setMonth(value)} aria-label={tt("month")} />
          </label>
          <Btn variant="primary" icon="layers" onClick={() => setOpen("monthly")}>
            {tt("openReport")}
          </Btn>
        </Panel>
        <Panel title={tt("report_products")} hint={tt("report_products_hint")}>
          <p className="an-muted an-small">
            {formatDate(range.dateFrom, lang)} – {formatDate(range.dateTo, lang)}
          </p>
          <Btn variant="primary" onClick={() => setOpen("products")}>
            {tt("openReport")}
          </Btn>
        </Panel>
        <Panel title={tt("report_inventory")} hint={tt("report_inventory_hint")}>
          <p className="an-muted an-small">{tt("inventorySnapshot")}</p>
          <Btn variant="primary" onClick={() => setOpen("inventory")}>
            {tt("openReport")}
          </Btn>
        </Panel>
      </div>
      <Panel title={tt("exportsTitle")} hint={tt("reportsHint")}>
        <ul className="an-exports">
          {EXPORTS.map((dataset) => (
            <li key={dataset}>
              <Icon name="download" size={16} />
              <span>{tt(`export_${dataset}`)}</span>
              <ExportButton dataset={dataset} params={dataset === "inventory" ? {} : { dateFrom: range.dateFrom, dateTo: range.dateTo }} tt={tt} />
            </li>
          ))}
        </ul>
      </Panel>
      <MonthlyTable range={range} tt={tt} lang={lang} costVisible={costVisible} />
    </>
  );
};

export default ReportsSection;
